import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    status: {
      danger: React.CSSProperties['color'];
    };
  }
  interface ThemeOptions {
    status?: {
      danger?: React.CSSProperties['color'];
    };
  }
}

const theme = createTheme({
  components: {
    // https://mui.com/material-ui/customization/theme-components/
    MuiInputBase: {
      styleOverrides: {
        root: {
          border: '1px solid #E6E8F3',
          padding: '6px 12px',
          borderRadius: '14px',
          height: '60px',
          // color: '#555555',
          '&.Mui-focused': {
            boxShadow: '0px 0px 4px rgba(0,0,0,0.01), 0px 8px 16px rgba(0,0,0,0.02)'
          },
          '&.Mui-disabled': {
            backgroundColor: '#f0f0f0',
            cursor: 'not-allowed !important',
          }
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#2D94E8',
          },
          '&.MuiCheckbox-indeterminate': {
            color: '#2D94E8',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          border: '1px solid #E6E8F3',
          padding: '6px 12px',
          borderRadius: '14px',
          height: '60px',
          '& .MuiInputBase-input': {
            paddingLeft: '0px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none', // Remove the default border
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #2D94E8',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #2D94E8',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '14px',
          border: '1px solid #E6E8F3',
          boxShadow: '0px 8px 16px rgba(0,0,0,0.2)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#EAF5FD',
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 480,
      md: 768,
      lg: 992,
      xl: 1440,
    },
  },
  typography: {
    fontFamily: 'Figtree, sans-serif',
    h1: {
      color: '#FFF',
      textAlign: 'center',
      fontFamily: 'Manrope, sans-serif',
      fontWeight: 800,
      fontSize: '58px',
      lineHeight: '120%',
      letterSpacing: '-2.32px',
      '@media (max-width: 991px)': {
        fontSize: '36px',
        lineHeight: '41.8px',
        letterSpacing: '-1.44px'
      },
    },
    h2: {
      color: '#FFF',
      textAlign: 'center',
      fontFamily: 'Manrope, sans-serif',
      fontWeight: 800,
      fontSize: '42px',
      '@media (max-width: 991px)': {
        fontSize: '29px',
        lineHeight: 'normal'
      },
    },
    h3: {
      color: '#04193B',
      fontFamily: 'Figtree, sans-serif',
      fontWeight: 700,
      fontSize: '35px',
      lineHeight: '1.1',
    },
    h4: {
      fontFamily: 'Manrope, sans-serif',
      fontSize: '22px',
      fontWeight: 800,
      lineHeight: '140%',
      letterSpacing: '-0.66px',
      color: '#04193B',
    },
    h5: {
      fontFamily: 'Figtree, sans-serif',
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '135%',
      letterSpacing: '-0.238px',
      color: '#000',
      '@media (max-width: 991px)': {
        fontSize: '16px',
        letterSpacing: '-0.16px',
      },
    },
    h6: {
      fontFamily: 'Figtree, sans-serif',
      fontSize: '15px',
      fontWeight: 700,
      lineHeight: '135%',
      letterSpacing: '1.5px',
      color: '#2d94e8',
      '@media (max-width: 991px)': {
        fontSize: '16px',
        lineHeight: '17.78px',
        letterSpacing: '1.6px',
      },
    },
    subtitle1: {
      color: '#1B1D37',
      textAlign: 'center',
      fontFamily: 'Manrope, sans-serif',
      fontSize: '40px',
      fontWeight: 800,
      lineHeight: '120%',
      letterSpacing: '-1.2px',
      '@media (max-width: 991px)': {
        color: '#031430',
        fontSize: '29px',
        lineHeight: '41.8px',
        letterSpacing: '-1px',
      },
    },
    subtitle2: {
      fontFamily: 'Figtree, sans-serif',
      fontSize: '18px',
      fontWeight: 700,
      lineHeight: '25.7143px',
      color: '#04193B',
    },
    body1: {
      color: '#35363B',
      fontFamily: 'Figtree, sans-serif',
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: '160%',
      letterSpacing: '-0.18px',
      textAlign: 'left',
      '@media (max-width: 991px)': {
        fontSize: '16px',
        lineHeight: '26.67px',
      },
    },
    button: {
      color: '#04193B',
      fontFamily: 'Manrope, sans-serif',
      fontSize: '18px',
      fontWeight: 800,
      lineHeight: '140%',
      letterSpacing: '-0.54px',
      textAlign: 'left',
      textTransform: 'unset',
      '@media (max-width: 991px)': {
        fontSize: '16px',
        letterSpacing: '-0.48px',
      },
    },
    caption: {
      fontSize: '14px',
      fontFamily: 'Figtree, sans-serif',
      color: '#35363B',
      lineHeight: '20px',
    },
    // not used yet
    overline: {
      fontSize: '0.75rem',
      textTransform: 'uppercase',
    },
  },
  palette: {
    primary: {
      main: '#04193B',
    },
    secondary: {
      main: '#dc004e',
    },
    error: {
      main: '#f44336',
    },
  },
  status: {
    danger: '#ff0000',
  },
});

export default theme;
